import type { Axis, DeviationRow, Granularity, PeriodPick, TimeStudyFilters } from './types';
import {
  endOfBusinessDayIso,
  formatDate,
  formatPeriod,
  inclusiveDays,
  periodSpan,
  startOfBusinessDayIso,
} from '@/lib/time';

// Presentación pura de Estudio de tiempos (so=420, spec §4.2 y D7): horas con una
// decimal y coma, desviación con su tono, períodos en español y las consultas que
// arman las tarjetas a partir de los filtros de la URL.

export const AXIS_LABELS: Readonly<Record<Axis, string>> = {
  person: 'Persona',
  documentType: 'Tipo de documento',
  processArea: 'Proceso',
  department: 'Departamento',
};

export const GRANULARITY_LABELS: Readonly<Record<Granularity, string>> = {
  day: 'Día',
  month: 'Mes',
  year: 'Año',
};

const ONE_DECIMAL = new Intl.NumberFormat('es-CR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  useGrouping: false,
});

export function formatHours(seconds: number): string {
  return `${ONE_DECIMAL.format(seconds / 3600)} h`;
}

/** Duración legible en horas y minutos ("6 min", "389 h 54 min"), para tiempos en curso
 *  de una persona: la hora con una decimal esconde los minutos. */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  if (total > 0 && total < 60) {
    return 'menos de 1 min';
  }
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h === 0) {
    return `${m} min`;
  }
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** `+43,8 %`, `−15,0 %` (signo menos tipográfico), `0,0 %`; null = sin estándar. */
export function formatPct(pct: number | null): string {
  if (pct === null) {
    return 'Sin estándar';
  }
  const abs = ONE_DECIMAL.format(Math.abs(pct));
  if (pct > 0) {
    return `+${abs} %`;
  }
  return pct < 0 ? `−${abs} %` : `${abs} %`;
}

/** Proporción con una decimal (`57,1 %`); null = sin etapas, se muestra vacío. */
export function formatShare(pct: number | null): string {
  return pct === null ? '—' : `${ONE_DECIMAL.format(pct)} %`;
}

export type Tone = 'ok' | 'warn' | 'alert' | 'none';

/** Hasta 0 % en tono positivo, hasta +20 % advertencia, más de +20 % alerta. */
export function deviationTone(pct: number | null): Tone {
  if (pct === null) {
    return 'none';
  }
  if (pct <= 0) {
    return 'ok';
  }
  return pct <= 20 ? 'warn' : 'alert';
}

/** Etiqueta del período en los gráficos: día "28/09", mes "sep 2026", año "2026". */
export function periodLabel(period: string, gran: Granularity): string {
  return formatPeriod(period, gran);
}

const INVERTED = 'La fecha inicial es posterior a la final.';

/** Rango de desviación y reprocesos: ordenado y de hasta 366 días (topes del backend). */
export function rangeProblem(f: TimeStudyFilters): string | null {
  if (f.desde > f.hasta) {
    return INVERTED;
  }
  return inclusiveDays(f.desde, f.hasta) > 366
    ? 'Los indicadores y el análisis admiten hasta 366 días. Documentos por período muestra el rango completo por mes o por año.'
    : null;
}

/** Topes de documentos por período (so=420 §3.3): 92 días, 36 meses o 10 años. */
export function throughputProblem(f: TimeStudyFilters): string | null {
  if (f.desde > f.hasta) {
    return INVERTED;
  }
  const span = periodSpan(f.gran, f.desde, f.hasta);
  if (f.gran === 'day') {
    return span > 92 ? 'Por día se muestran hasta 92 días. Cambia a mes o acota las fechas.' : null;
  }
  if (f.gran === 'month') {
    return span > 36
      ? 'Por mes se muestran hasta 36 meses. Cambia a año o acota las fechas.'
      : null;
  }
  return span > 10 ? 'Por año se muestran hasta 10 años. Acota las fechas.' : null;
}

function withFilters(p: URLSearchParams, f: TimeStudyFilters): string {
  if (f.tipo !== null) {
    p.set('documentTypeId', f.tipo);
  }
  if (f.area !== null) {
    p.set('processAreaId', f.area);
  }
  return p.toString();
}

/** Desviación y reprocesos: del inicio de `desde` al último instante de `hasta` en CR. */
export function analysisQuery(f: TimeStudyFilters): string {
  return withFilters(
    new URLSearchParams({ from: startOfBusinessDayIso(f.desde), to: endOfBusinessDayIso(f.hasta) }),
    f,
  );
}

/** Detalle de un período (G1-7): la consulta de la barra más el período y la serie. */
export function throughputDocumentsQuery(f: TimeStudyFilters, pick: PeriodPick): string {
  const p = new URLSearchParams(throughputQuery(f));
  p.set('period', pick.period);
  p.set('series', pick.series);
  return p.toString();
}

/** Período completo para títulos: "del 28/09/2026", "en sep 2026", "en 2026". */
export function periodPhrase(period: string, gran: Granularity): string {
  return gran === 'day' ? `del ${formatDate(period)}` : `en ${formatPeriod(period, gran)}`;
}

/** Cumplimiento de plazos (G1-5): días de negocio `AAAA-MM-DD`, el backend los corta en CR. */
export function complianceQuery(f: TimeStudyFilters): string {
  return withFilters(new URLSearchParams({ from: f.desde, to: f.hasta }), f);
}

export function throughputQuery(f: TimeStudyFilters): string {
  return withFilters(new URLSearchParams({ granularity: f.gran, from: f.desde, to: f.hasta }), f);
}

/** Esfuerzo comparable: con estándar, solo el de los trabajos que lo tienen. */
export const realOf = (r: DeviationRow): number =>
  r.standardSeconds === null ? r.effortSeconds : r.effortWithStandardSeconds;
